import type { AuthResponse, MeDto } from '@hudhuria/shared';
import { useSyncExternalStore } from 'react';
import { storage } from '~/lib/storage';

/** This device is a front desk: staff at `centerId` sign in on it by name + PIN. */
export interface DeskInfo {
  id: string;
  token: string;
  name: string;
  centerId: string;
  centerName: string;
}

/**
 * Client session: bearer token, chosen center, front-desk setup and first-launch flags.
 * The `me` profile lives in the query cache.
 */
export interface SessionSnapshot {
  token: string | null;
  centerId: string | null;
  onboarded: boolean;
  desk: DeskInfo | null;
}

let snapshot: SessionSnapshot = { token: null, centerId: null, onboarded: false, desk: null };

function readDesk(): DeskInfo | null {
  try {
    const raw = storage.get('desk');
    return raw ? (JSON.parse(raw) as DeskInfo) : null;
  } catch {
    return null;
  }
}
const listeners = new Set<() => void>();

function refresh() {
  snapshot = {
    token: storage.get('token'),
    centerId: storage.get('center'),
    onboarded: storage.get('onboarded') === '1',
    desk: readDesk(),
  };
  for (const l of listeners) l();
}

export const session = {
  init: refresh,
  get: () => snapshot,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Store a new session. Picks the center automatically when the staff member has exactly one. */
  signIn(res: AuthResponse) {
    storage.set('token', res.token);
    storage.set('onboarded', '1'); // signed in (or accepted an invite) — no need for the intro
    const keep = snapshot.centerId && res.me.centers.some((c) => c.id === snapshot.centerId);
    if (!keep) storage.set('center', res.me.centers.length === 1 ? res.me.centers[0]!.id : null);
    refresh();
  },
  signOut() {
    storage.set('token', null);
    storage.set('center', null);
    refresh();
  },
  /** Token rejected by the server: drop it but keep the chosen center for the next sign-in. */
  expire() {
    storage.set('token', null);
    refresh();
  },
  setCenter(id: string | null) {
    storage.set('center', id);
    refresh();
  },
  setDesk(desk: DeskInfo | null) {
    storage.set('desk', desk ? JSON.stringify(desk) : null);
    refresh();
  },
  setOnboarded() {
    storage.set('onboarded', '1');
    refresh();
  },
  /** true when the staff member must choose a center before entering the app */
  needsCenter(me: MeDto | undefined) {
    if (!me) return false;
    const id = snapshot.centerId;
    return !id || !me.centers.some((c) => c.id === id);
  },
};

export function useSession() {
  return useSyncExternalStore(session.subscribe, session.get);
}
